import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Trash2, List } from "lucide-react";

const LightLeakageInspection = () => {
  const { state } = useLocation();
  const {
    productInfo,
    imageUrl,
    rootCauses: passedRootCauses = [],
    recordingNotes: passedNotes = "",
    rectangles: passedRectangles = [],
    yoloAnnotations: passedYoloAnnotations = [],
    imageDimensions: passedImageDimensions = { width: 0, height: 0 },
    canvasTransform: passedCanvasTransform = { scale: 1, offsetX: 0, offsetY: 0, scaledWidth: 0, scaledHeight: 0 },
  } = state || {};

  const [rootCauses, setRootCauses] = useState(passedRootCauses);
  const [recordingNotes, setRecordingNotes] = useState(passedNotes || "");
  const [rectangles, setRectangles] = useState(() => {
    const uniqueRectangles = [...passedRectangles].reduce((acc, rect) => {
      if (
        rect &&
        Number.isFinite(rect.x) &&
        Number.isFinite(rect.y) &&
        Number.isFinite(rect.width) &&
        Number.isFinite(rect.height) &&
        rect.width > 0 &&
        rect.height > 0 &&
        !acc.some(
          (r) =>
            r.x === rect.x &&
            r.y === rect.y &&
            r.width === rect.width &&
            r.height === rect.height
        )
      ) {
        acc.push(rect);
      } else {
        console.warn("Invalid or duplicate rectangle filtered:", rect);
      }
      return acc;
    }, []);
    console.log("Initialized rectangles:", uniqueRectangles);
    return uniqueRectangles;
  });
  const [yoloAnnotations, setYoloAnnotations] = useState(passedYoloAnnotations);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startCoords, setStartCoords] = useState(null);
  const [selectedRectIndex, setSelectedRectIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);
  const [resizingHandle, setResizingHandle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedColor, setSelectedColor] = useState("#ffffff");
  const [showYoloValues, setShowYoloValues] = useState(false);
  const canvasRef = useRef(null);
  const [isInsideImage, setIsInsideImage] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({
    width: passedImageDimensions.width,
    height: passedImageDimensions.height,
  });
  const [canvasTransform, setCanvasTransform] = useState(passedCanvasTransform);
  const [image, setImage] = useState(null);
  const navigate = useNavigate();
  const HANDLE_SIZE = 15;
  const PADDING = 20;
  const BG_COLOR = "black";

  const convertToYOLOFormat = (rects, imgWidth, imgHeight) => {
    const annotations = rects
      .map((rect, index) => {
        if (
          !rect ||
          !Number.isFinite(rect.x) ||
          !Number.isFinite(rect.y) ||
          !Number.isFinite(rect.width) ||
          !Number.isFinite(rect.height) ||
          rect.width <= 0 ||
          rect.height <= 0 ||
          imgWidth <= 0 ||
          imgHeight <= 0
        ) {
          console.warn(`Invalid rectangle at index ${index}:`, rect);
          return null;
        }

        const x_center = (rect.x + rect.width / 2) / imgWidth;
        const y_center = (rect.y + rect.height / 2) / imgHeight;
        const width = rect.width / imgWidth;
        const height = rect.height / imgHeight;

        if (
          !Number.isFinite(x_center) ||
          !Number.isFinite(y_center) ||
          !Number.isFinite(width) ||
          !Number.isFinite(height)
        ) {
          console.warn(`NaN values in YOLO conversion at index ${index}:`, {
            x_center,
            y_center,
            width,
            height,
            rect,
          });
          return null;
        }

        const formattedX = x_center.toFixed(6);
        const formattedY = y_center.toFixed(6);
        const formattedW = width.toFixed(6);
        const formattedH = height.toFixed(6);

        return {
          x_center: parseFloat(formattedX),
          y_center: parseFloat(formattedY),
          width: parseFloat(formattedW),
          height: parseFloat(formattedH),
          color: rect.color,
          displayText: `(${formattedX}, ${formattedY}, ${formattedW}, ${formattedH})`,
          formattedValues: {
            x: formattedX,
            y: formattedY,
            w: formattedW,
            h: formattedH,
          },
        };
      })
      .filter((annotation) => annotation !== null);

    console.log("Generated YOLO Annotations:", annotations);
    return annotations;
  };

  useEffect(() => {
    console.log("Received state:", state);
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImage(img);
      setImageDimensions({ width: img.width, height: img.height });

      const updateCanvasTransform = () => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const canvasWidth = window.innerWidth * window.devicePixelRatio;
        const canvasHeight = window.innerHeight * window.devicePixelRatio;

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        canvas.style.width = `${window.innerWidth}px`;
        canvas.style.height = `${window.innerHeight}px`;

        const BOTTOM_PADDING = 50;
        const scale = Math.min(
          (canvasWidth - PADDING * 2) / img.width,
          (canvasHeight - PADDING * 2 - BOTTOM_PADDING) / img.height
        );
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const offsetX = (canvasWidth - scaledWidth) / 2;
        const offsetY = (canvasHeight - scaledHeight - BOTTOM_PADDING) / 2;

        setCanvasTransform({
          scale,
          offsetX,
          offsetY,
          scaledWidth,
          scaledHeight,
        });
      };

      updateCanvasTransform();
      window.addEventListener("resize", updateCanvasTransform);

      // Update YOLO annotations based on initial rectangles
      setYoloAnnotations(convertToYOLOFormat(rectangles, img.width, img.height));

      return () => {
        window.removeEventListener("resize", updateCanvasTransform);
      };
    };
  }, [imageUrl, rectangles]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      redrawCanvas(ctx);
    }
  }, [
    rectangles,
    yoloAnnotations,
    selectedRectIndex,
    mousePosition,
    isInsideImage,
    isDrawing,
    startCoords,
    selectedColor,
    canvasTransform,
    image,
    showYoloValues,
  ]);

  const getCanvasCoordinates = (clientX, clientY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const x = ((clientX - rect.left) * canvas.width) / rect.width;
    const y = ((clientY - rect.top) * canvas.height) / rect.height;

    return { x, y };
  };

  const getImageCoordinates = (x, y) => {
    const { scale, offsetX, offsetY } = canvasTransform;
    const imgX = (x - offsetX) / scale;
    const imgY = (y - offsetY) / scale;
    return { imgX, imgY };
  };

  const isWithinImageBounds = (x, y) => {
    const { offsetX, offsetY, scaledWidth, scaledHeight } = canvasTransform;
    return (
      x >= offsetX &&
      x <= offsetX + scaledWidth &&
      y >= offsetY &&
      y <= offsetY + scaledHeight
    );
  };

  const redrawCanvas = (ctx) => {
    if (!ctx || !image) return;

    const canvas = ctx.canvas;
    const { scale, offsetX, offsetY, scaledWidth, scaledHeight } = canvasTransform;

    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(image, offsetX, offsetY, scaledWidth, scaledHeight);

    if (isDrawing && startCoords && mousePosition) {
      const { imgX: endX, imgY: endY } = getImageCoordinates(
        mousePosition.x,
        mousePosition.y
      );

      const x = Math.min(startCoords.x, endX);
      const y = Math.min(startCoords.y, endY);
      const width = Math.abs(endX - startCoords.x);
      const height = Math.abs(endY - startCoords.y);

      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = 8;
      ctx.lineJoin = "round";
      ctx.strokeRect(
        offsetX + x * scale,
        offsetY + y * scale,
        width * scale,
        height * scale
      );
    }

    drawRectangles(ctx);
    drawCrosshair(ctx);
    drawCoordinates(ctx);
  };

  const drawRectangles = (ctx) => {
    const { scale, offsetX, offsetY } = canvasTransform;

    const annotations = showYoloValues ? yoloAnnotations : [];

    rectangles.forEach((rect, index) => {
      ctx.strokeStyle = rect.color || "#ffffff";
      ctx.lineWidth = index === selectedRectIndex ? 4 : 2;
      ctx.lineJoin = "round";
      ctx.strokeRect(
        offsetX + rect.x * scale,
        offsetY + rect.y * scale,
        rect.width * scale,
        rect.height * scale
      );

      if (index === selectedRectIndex) {
        ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
        ctx.fillRect(
          offsetX + rect.x * scale,
          offsetY + rect.y * scale,
          rect.width * scale,
          rect.height * scale
        );

        const handles = getResizeHandles(rect);
        ctx.fillStyle = "white";
        handles.forEach((handle) => {
          ctx.fillRect(handle.x, handle.y, HANDLE_SIZE, HANDLE_SIZE);
        });
      }

      if (showYoloValues && annotations[index]) {
        const centerX = offsetX + (rect.x + rect.width / 2) * scale;
        const centerY = offsetY + (rect.y + rect.height / 2) * scale;
        const formatted = annotations[index].formattedValues;

        ctx.font = "12px Arial";
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.fillText(
          `x: ${formatted.x}, y: ${formatted.y}`,
          centerX,
          centerY - 10
        );
        ctx.fillText(`w: ${formatted.w}, h: ${formatted.h}`, centerX, centerY + 10);
      }
    });
  };

  const getResizeHandles = (rect) => {
    if (!rect) return [];
    const { scale, offsetX, offsetY } = canvasTransform;
    const x = offsetX + rect.x * scale;
    const y = offsetY + rect.y * scale;
    const width = rect.width * scale;
    const height = rect.height * scale;

    return [
      { x: x - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "nw" },
      { x: x + width - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "ne" },
      { x: x - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "sw" },
      { x: x + width - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "se" },
      { x: x + width / 2 - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2, position: "n" },
      { x: x + width / 2 - HANDLE_SIZE / 2, y: y + height - HANDLE_SIZE / 2, position: "s" },
      { x: x - HANDLE_SIZE / 2, y: y + height / 2 - HANDLE_SIZE / 2, position: "w" },
      { x: x + width - HANDLE_SIZE / 2, y: y + height / 2 - HANDLE_SIZE / 2, position: "e" },
    ];
  };

  const getCursorForHandle = (handlePosition) => {
    switch (handlePosition) {
      case "n":
      case "s":
        return "ns-resize";
      case "e":
      case "w":
        return "ew-resize";
      case "ne":
      case "sw":
        return "nesw-resize";
      case "nw":
      case "se":
        return "nwse-resize";
      default:
        return "move";
    }
  };

  const drawCrosshair = (ctx) => {
    if (!mousePosition) return;
    const gapSize = 35;
    const centerX = mousePosition.x;
    const centerY = mousePosition.y;
    const dotRadius = 5;

    ctx.save();
    ctx.fillStyle = "white";
    ctx.strokeStyle = isDrawing || isDragging || resizingHandle ? "white" : "white";
    ctx.lineWidth = isDrawing || isDragging || resizingHandle ? 2 : 2;
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, centerY - gapSize);
    ctx.moveTo(centerX, centerY + gapSize);
    ctx.lineTo(centerX, ctx.canvas.height);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(centerX - gapSize, centerY);
    ctx.moveTo(centerX + gapSize, centerY);
    ctx.lineTo(ctx.canvas.width, centerY);
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = "white";
    ctx.arc(centerX, centerY, dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const drawCoordinates = (ctx) => {
    if (!mousePosition) return;
    const { x, y } = mousePosition;
    const { scale, offsetX, offsetY } = canvasTransform;
    const banOffsetX = 25;
    const banOffsetY = 25;
    const banRadius = 20;
    const banX = x + banOffsetX;
    const banY = y + banOffsetY;

    if (!isInsideImage) {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 4;
      ctx.arc(banX, banY, banRadius, 0, Math.PI * 2);
      ctx.stroke();
      const crossLength = 15;
      ctx.beginPath();
      ctx.lineWidth = 4;
      ctx.moveTo(banX - crossLength, banY - crossLength);
      ctx.lineTo(banX + crossLength, banY + crossLength);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.save();
      const { imgX, imgY } = getImageCoordinates(x, y);
      const text = `x: ${imgX.toFixed(0)}, y: ${imgY.toFixed(0)}`;
      const fontSize = 12;
      ctx.font = `bold ${fontSize}px Arial`;
      const paddingBox = 10;
      const textWidth = ctx.measureText(text).width;
      const boxWidth = textWidth + paddingBox * 2;
      const boxHeight = fontSize + paddingBox * 2;
      let boxX = x + banOffsetX;
      let boxY = y + banOffsetY;
      if (boxX + boxWidth > ctx.canvas.width) {
        boxX = x - boxWidth - banOffsetX;
      }
      if (boxY < 0) {
        boxY = y - banOffsetY;
      }
      if (boxY + boxHeight > ctx.canvas.height) {
        boxY = y - boxHeight - banOffsetY;
      }
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
      ctx.fillStyle = "white";
      ctx.textBaseline = "top";
      ctx.fillText(text, boxX + paddingBox, boxY + paddingBox);
      ctx.restore();
    }
  };

  const getHandleAtPosition = (x, y) => {
    if (selectedRectIndex === null || selectedRectIndex >= rectangles.length)
      return null;
    const rect = rectangles[selectedRectIndex];
    const handles = getResizeHandles(rect);
    for (const handle of handles) {
      if (
        x >= handle.x &&
        x <= handle.x + HANDLE_SIZE &&
        y >= handle.y &&
        y <= handle.y + HANDLE_SIZE
      ) {
        return handle.position;
      }
    }
    return null;
  };

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    const { imgX, imgY } = getImageCoordinates(x, y);

    if (!isWithinImageBounds(x, y)) {
      setIsInsideImage(false);
      return;
    }

    const handlePosition = getHandleAtPosition(x, y);
    if (handlePosition) {
      setResizingHandle(handlePosition);
      return;
    }

    let clickedIndex = null;
    rectangles.forEach((rect, index) => {
      const rectX = canvasTransform.offsetX + rect.x * canvasTransform.scale;
      const rectY = canvasTransform.offsetY + rect.y * canvasTransform.scale;
      const rectWidth = rect.width * canvasTransform.scale;
      const rectHeight = rect.height * canvasTransform.scale;
      if (
        x >= rectX &&
        x <= rectX + rectWidth &&
        y >= rectY &&
        y <= rectY + rectHeight
      ) {
        clickedIndex = index;
      }
    });

    if (clickedIndex !== null) {
      setSelectedRectIndex(clickedIndex);
      setIsDragging(true);
      setDragOffset({
        x: imgX - rectangles[clickedIndex].x,
        y: imgY - rectangles[clickedIndex].y,
      });
    } else {
      setIsDrawing(true);
      setStartCoords({ x: imgX, y: imgY });
      setSelectedRectIndex(null);
    }
  };

  const handleMouseMove = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    const { imgX, imgY } = getImageCoordinates(x, y);

    setMousePosition({ x, y });

    const insideImage =
      imgX >= 0 &&
      imgX <= imageDimensions.width &&
      imgY >= 0 &&
      imgY <= imageDimensions.height;
    setIsInsideImage(insideImage);

    if (selectedRectIndex !== null && !resizingHandle && !isDragging) {
      const handlePosition = getHandleAtPosition(x, y);
      canvasRef.current.style.cursor = handlePosition
        ? getCursorForHandle(handlePosition)
        : "default";
    }

    if (resizingHandle && selectedRectIndex !== null) {
      const rect = rectangles[selectedRectIndex];
      const newRect = { ...rect };

      switch (resizingHandle) {
        case "nw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = rect.y + rect.height - imgY;
          newRect.x = imgX;
          newRect.y = imgY;
          break;
        case "ne":
          newRect.width = imgX - rect.x;
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "sw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = imgY - rect.y;
          newRect.x = imgX;
          break;
        case "se":
          newRect.width = imgX - rect.x;
          newRect.height = imgY - rect.y;
          break;
        case "n":
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "s":
          newRect.height = imgY - rect.y;
          break;
        case "e":
          newRect.width = imgX - rect.x;
          break;
        case "w":
          newRect.width = rect.x + rect.width - imgX;
          newRect.x = imgX;
          break;
      }

      if (newRect.width < 5 / canvasTransform.scale)
        newRect.width = 5 / canvasTransform.scale;
      if (newRect.height < 5 / canvasTransform.scale)
        newRect.height = 5 / canvasTransform.scale;

      const newRectangles = [...rectangles];
      newRectangles[selectedRectIndex] = newRect;
      setRectangles(newRectangles);
      setYoloAnnotations(convertToYOLOFormat(newRectangles, imageDimensions.width, imageDimensions.height));
    }

    if (isDragging && selectedRectIndex !== null) {
      const newRectangles = [...rectangles];
      const rect = newRectangles[selectedRectIndex];
      const newX = imgX - dragOffset.x;
      const newY = imgY - dragOffset.y;

      rect.x = Math.max(0, Math.min(newX, imageDimensions.width - rect.width));
      rect.y = Math.max(0, Math.min(newY, imageDimensions.height - rect.height));
      setRectangles(newRectangles);
      setYoloAnnotations(convertToYOLOFormat(newRectangles, imageDimensions.width, imageDimensions.height));
    }

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      redrawCanvas(ctx);
    }
  };

  const handleMouseUp = (e) => {
    if (resizingHandle) {
      setResizingHandle(null);
      return;
    }

    if (isDragging) {
      setIsDragging(false);
      return;
    }

    if (!isDrawing || !startCoords || !mousePosition) return;

    const { x, y } = getCanvasCoordinates(e.clientX, e.clientY);
    if (!isWithinImageBounds(x, y)) {
      setIsDrawing(false);
      setStartCoords(null);
      return;
    }

    const { imgX, imgY } = getImageCoordinates(mousePosition.x, mousePosition.y);
    const clampedX = Math.max(0, Math.min(imgX, imageDimensions.width));
    const clampedY = Math.max(0, Math.min(imgY, imageDimensions.height));

    const width = Math.abs(clampedX - startCoords.x);
    const height = Math.abs(clampedY - startCoords.y);
    if (
      width > 5 / canvasTransform.scale &&
      height > 5 / canvasTransform.scale &&
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      Number.isFinite(startCoords.x) &&
      Number.isFinite(startCoords.y)
    ) {
      const newRectangle = {
        x: Math.min(startCoords.x, clampedX),
        y: Math.min(startCoords.y, clampedY),
        width,
        height,
        color: selectedColor,
      };

      setRectangles((prev) => {
        const newRects = [...prev, newRectangle];
        console.log("New rectangles after mouseUp:", newRects);
        setYoloAnnotations(convertToYOLOFormat(newRects, imageDimensions.width, imageDimensions.height));
        return newRects;
      });
      setSelectedRectIndex(rectangles.length);
    } else {
      console.warn("Invalid rectangle detected, not adding:", {
        width,
        height,
        startCoords,
        clampedX,
        clampedY,
      });
    }

    setIsDrawing(false);
    setStartCoords(null);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (!canvasRef.current || e.touches.length === 0) return;

    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    const { imgX, imgY } = getImageCoordinates(x, y);

    if (!isWithinImageBounds(x, y)) {
      setIsInsideImage(false);
      return;
    }

    setMousePosition({ x, y });

    const handlePosition = getHandleAtPosition(x, y);
    if (handlePosition) {
      setResizingHandle(handlePosition);
      return;
    }

    let clickedIndex = null;
    rectangles.forEach((rect, index) => {
      const rectX = canvasTransform.offsetX + rect.x * canvasTransform.scale;
      const rectY = canvasTransform.offsetY + rect.y * canvasTransform.scale;
      const rectWidth = rect.width * canvasTransform.scale;
      const rectHeight = rect.height * canvasTransform.scale;
      if (
        x >= rectX &&
        x <= rectX + rectWidth &&
        y >= rectY &&
        y <= rectY + rectHeight
      ) {
        clickedIndex = index;
      }
    });

    if (clickedIndex !== null) {
      setSelectedRectIndex(clickedIndex);
      setIsDragging(true);
      setDragOffset({
        x: imgX - rectangles[clickedIndex].x,
        y: imgY - rectangles[clickedIndex].y,
      });
    } else {
      setIsDrawing(true);
      setStartCoords({ x: imgX, y: imgY });
      setSelectedRectIndex(null);
    }
  };

  const handleTouchMove = (e) => {
    if (isDrawing || isDragging || resizingHandle) {
      e.preventDefault();
    }

    if (!canvasRef.current || e.touches.length === 0) return;

    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(touch.clientX, touch.clientY);
    const { imgX, imgY } = getImageCoordinates(x, y);

    setMousePosition({ x, y });

    const insideImage =
      imgX >= 0 &&
      imgX <= imageDimensions.width &&
      imgY >= 0 &&
      imgY <= imageDimensions.height;
    setIsInsideImage(insideImage);

    if (resizingHandle && selectedRectIndex !== null) {
      const rect = rectangles[selectedRectIndex];
      const newRect = { ...rect };

      switch (resizingHandle) {
        case "nw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = rect.y + rect.height - imgY;
          newRect.x = imgX;
          newRect.y = imgY;
          break;
        case "ne":
          newRect.width = imgX - rect.x;
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "sw":
          newRect.width = rect.x + rect.width - imgX;
          newRect.height = imgY - rect.y;
          newRect.x = imgX;
          break;
        case "se":
          newRect.width = imgX - rect.x;
          newRect.height = imgY - rect.y;
          break;
        case "n":
          newRect.height = rect.y + rect.height - imgY;
          newRect.y = imgY;
          break;
        case "s":
          newRect.height = imgY - rect.y;
          break;
        case "e":
          newRect.width = imgX - rect.x;
          break;
        case "w":
          newRect.width = rect.x + rect.width - imgX;
          newRect.x = imgX;
          break;
      }

      if (newRect.width < 5 / canvasTransform.scale)
        newRect.width = 5 / canvasTransform.scale;
      if (newRect.height < 5 / canvasTransform.scale)
        newRect.height = 5 / canvasTransform.scale;

      const newRectangles = [...rectangles];
      newRectangles[selectedRectIndex] = newRect;
      setRectangles(newRectangles);
      setYoloAnnotations(convertToYOLOFormat(newRectangles, imageDimensions.width, imageDimensions.height));
    }

    if (isDragging && selectedRectIndex !== null) {
      const newRectangles = [...rectangles];
      const rect = newRectangles[selectedRectIndex];
      const newX = imgX - dragOffset.x;
      const newY = imgY - dragOffset.y;

      rect.x = Math.max(0, Math.min(newX, imageDimensions.width - rect.width));
      rect.y = Math.max(0, Math.min(newY, imageDimensions.height - rect.height));
      setRectangles(newRectangles);
      setYoloAnnotations(convertToYOLOFormat(newRectangles, imageDimensions.width, imageDimensions.height));
    }

    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      redrawCanvas(ctx);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();

    if (resizingHandle) {
      setResizingHandle(null);
      return;
    }

    if (isDragging) {
      setIsDragging(false);
      return;
    }

    if (!isDrawing || !startCoords || !mousePosition) return;

    const { x, y } = getCanvasCoordinates(
      e.changedTouches[0]?.clientX || mousePosition.x,
      e.changedTouches[0]?.clientY || mousePosition.y
    );
    if (!isWithinImageBounds(x, y)) {
      setIsDrawing(false);
      setStartCoords(null);
      return;
    }

    const { imgX, imgY } = getImageCoordinates(mousePosition.x, mousePosition.y);
    const clampedX = Math.max(0, Math.min(imgX, imageDimensions.width));
    const clampedY = Math.max(0, Math.min(imgY, imageDimensions.height));

    const width = Math.abs(clampedX - startCoords.x);
    const height = Math.abs(clampedY - startCoords.y);
    if (
      width > 5 / canvasTransform.scale &&
      height > 5 / canvasTransform.scale &&
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      Number.isFinite(startCoords.x) &&
      Number.isFinite(startCoords.y)
    ) {
      const newRectangle = {
        x: Math.min(startCoords.x, clampedX),
        y: Math.min(startCoords.y, clampedY),
        width,
        height,
        color: selectedColor,
      };

      setRectangles((prev) => {
        const newRects = [...prev, newRectangle];
        console.log("New rectangles after touchEnd:", newRects);
        setYoloAnnotations(convertToYOLOFormat(newRects, imageDimensions.width, imageDimensions.height));
        return newRects;
      });
      setSelectedRectIndex(rectangles.length);
    } else {
      console.warn("Invalid rectangle detected in touchEnd:", {
        width,
        height,
        startCoords,
        clampedX,
        clampedY,
      });
    }

    setIsDrawing(false);
    setStartCoords(null);
  };

  const handleDelete = () => {
    if (selectedRectIndex === null || selectedRectIndex >= rectangles.length) return;

    // Remove the selected rectangle
    const newRectangles = rectangles.filter((_, i) => i !== selectedRectIndex);

    // Update rootCauses by removing any that reference the deleted rectangle
    const deletedRectIndex = selectedRectIndex;
    const newRootCauses = rootCauses.filter(
      (rc) => !rc.rectangleIds?.includes(deletedRectIndex)
    );

    // Adjust rectangleIds in remaining root causes
    const updatedRootCauses = newRootCauses.map((rc) => {
      if (!rc.rectangleIds) return rc;
      const newRectangleIds = rc.rectangleIds
        .map((id) => (id > deletedRectIndex ? id - 1 : id))
        .filter((id) => id < newRectangles.length);
      return { ...rc, rectangleIds: newRectangleIds };
    });

    // Update recording notes based on updated root causes
    const newRecordingNotes = updatedRootCauses
      .map((rc) => `${rc.leakageType}: ${rc.cause}`)
      .join("\n");

    // Update YOLO annotations
    const newYoloAnnotations = convertToYOLOFormat(
      newRectangles,
      imageDimensions.width,
      imageDimensions.height
    );

    setRectangles(newRectangles);
    setYoloAnnotations(newYoloAnnotations);
    setRootCauses(updatedRootCauses);
    setRecordingNotes(newRecordingNotes);
    setSelectedRectIndex(null);
  };

  const handleProceed = () => {
    if (rectangles.length === 0) {
      alert("Please annotate at least one defect before proceeding.");
      return;
    }

    navigate("/light-leakage-root-cause", {
      state: {
        productInfo,
        imageUrl,
        rootCauses,
        recordingNotes,
        rectangles,
        yoloAnnotations,
        imageDimensions,
        canvasTransform,
      },
    });
  };

  const handleBack = () => {
    navigate("/light-leakage-labeling", {
      state: {
        productInfo,
        imageUrl,
        rootCauses,
        recordingNotes,
        rectangles,
        yoloAnnotations,
        imageDimensions,
        canvasTransform,
      },
    });
  };

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden relative">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full bg-gray-700"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "none" }}
      />
      <div className="absolute inset-0 border-2 pointer-events-none" />
      <div className="absolute top-4 left-4 flex items-center space-x-4 pointer-events-auto">
        <button
          onClick={handleBack}
          className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700"
        >
          <ArrowLeft size={20} />
        </button>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-12 bg-black flex items-center justify-end space-x-4 text-white px-4 pointer-events-auto">
        <button
          onClick={handleDelete}
          className="p-1 hover:bg-red-500 rounded"
          disabled={selectedRectIndex === null}
        >
          <Trash2 size={20} />
        </button>
      
        <input
          type="color"
          value={selectedColor}
          onChange={(e) => {
            const newColor = e.target.value;
            setSelectedColor(newColor);
            if (selectedRectIndex !== null) {
              const updatedRectangles = [...rectangles];
              updatedRectangles[selectedRectIndex] = {
                ...updatedRectangles[selectedRectIndex],
                color: newColor,
              };
              setRectangles(updatedRectangles);
              setYoloAnnotations(convertToYOLOFormat(updatedRectangles, imageDimensions.width, imageDimensions.height));
            }
          }}
          className="h-6 w-6"
        />
        <button
          onClick={handleProceed}
          className="p-1 hover:bg-blue-500 rounded"
        >
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default LightLeakageInspection;

